import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/** An `sr-only` heading is 1px: there, but not something anyone can see. */
const isOnScreen = (el: Element) => {
  const rect = el.getBoundingClientRect();
  return rect.width > 1 && rect.height > 1;
};

/**
 * Whether the page's own title is out of sight, for the phone top bar.
 *
 * Every page opens with its h1, and the top bar named the page as well, so a
 * phone showed "Patients" twice, one above the other. This is the large-title
 * pattern from iOS instead: while the page's title is on screen the bar
 * leaves it to the page, and the name fades into the bar once the title
 * scrolls under it. A page whose title is hidden on phones (or has none)
 * gets the name in the bar from the start.
 *
 * Pages render their title after their data, so the heading is looked up
 * again whenever the content changes rather than once per route.
 */
export function useTitleScrolledAway(): boolean {
  const { pathname } = useLocation();
  const [away, setAway] = useState(true);

  useEffect(() => {
    const main = document.getElementById('main-content');
    if (!main) return;

    let heading: Element | null = null;
    let observer: IntersectionObserver | null = null;
    let frame = 0;

    const track = () => {
      frame = 0;
      const found = main.querySelector('h1');
      const next = found && isOnScreen(found) ? found : null;
      if (next === heading) return;
      heading = next;
      observer?.disconnect();
      observer = null;
      if (!heading) {
        setAway(true);
        return;
      }
      observer = new IntersectionObserver(
        ([entry]) => setAway(!entry.isIntersecting),
        { root: main },
      );
      observer.observe(heading);
    };

    // Coalesced to one lookup per frame: typing into a field mutates the
    // subtree on every keystroke.
    const mutations = new MutationObserver(() => {
      if (!frame) frame = requestAnimationFrame(track);
    });
    mutations.observe(main, { childList: true, subtree: true });
    track();

    return () => {
      mutations.disconnect();
      observer?.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [pathname]);

  return away;
}
